import React, { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import { PinLegendIcon } from "./PinLegendIcon";

interface LoadedMapPlot {
  id: number;
  name: string;
  /** このリストのピンの色 */
  color: string;
}

interface LoadedMapPlotBarProps {
  /** 地図上に読み込まれているプロットデータ（物件リスト）の配列 */
  mapPlots: LoadedMapPlot[];
  /**
   * ダウンロード実行時に親コンポーネントから呼び出されるコールバック
   * @param id - 物件リストID
   * @param name - 物件リスト名
   * @param formatType - ダウンロードするファイル形式 ('csv' | 'excel')
   */
  onDownload: (
    id: number,
    name: string,
    formatType: "csv" | "excel"
  ) => Promise<void>;
  /**
   * リストを地図から外す時に呼ぶ（保存した物件リスト自体は消さない）
   * @param id - 物件リストID
   */
  onRemove: (id: number) => void;
}

// 見た目は小さく、タップ領域の高さは 44px を確保する（当たり判定だけ広げる）
const iconButtonSx = {
  p: 0.25,
  color: "text.secondary",
  position: "relative",
  // 横はとなりのボタンと当たり判定が重ならない幅にとどめる
  "&::after": { content: '""', position: "absolute", inset: "-11px -2px" },
} as const;

/**
 * 表示中の物件リスト1つぶんの札。ピンの色・リスト名と、
 * CSV/Excel のダウンロードメニュー、地図から外すボタンを並べる
 */
const LoadedMapPlotChip: React.FC<{
  mapPlot: LoadedMapPlot;
  onDownload: (
    id: number,
    name: string,
    formatType: "csv" | "excel"
  ) => Promise<void>;
  onRemove: (id: number) => void;
}> = ({ mapPlot, onDownload, onRemove }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  /**
   * ダウンロードアイコンクリック時のイベントハンドラ
   * メニューを展開し、地図など親要素へのイベント伝播を防止します。
   */
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation(); // 地図クリック等のイベント発火を防ぐ
    setAnchorEl(event.currentTarget);
  };

  /**
   * メニューを閉じる際のイベントハンドラ
   */
  const handleClose = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  /**
   * CSV または Excel 形式でのダウンロードメニューが選択された際の処理
   */
  const handleDownloadClick = async (
    event: React.MouseEvent,
    formatType: "csv" | "excel"
  ): Promise<void> => {
    event.stopPropagation();
    setAnchorEl(null);
    await onDownload(mapPlot.id, mapPlot.name, formatType);
  };

  return (
    <>
      <Box
        data-testid="loaded-map-plot"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          height: "28px",
          pl: 1,
          pr: 0.25,
          border: 1,
          borderColor: mapPlot.color,
          borderRadius: "14px",
          maxWidth: "100%",
        }}
      >
        <PinLegendIcon kind="teardrop" color={mapPlot.color} />
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 500, fontSize: "0.85rem", minWidth: 0 }}
        >
          {mapPlot.name}
        </Typography>
        <Tooltip title="ダウンロード" arrow placement="top">
          <IconButton
            size="small"
            aria-label={`${mapPlot.name}をダウンロード`}
            onClick={handleClick}
            sx={iconButtonSx}
          >
            <FileDownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="地図から外す" arrow placement="top">
          <IconButton
            size="small"
            aria-label={`${mapPlot.name}を地図から外す`}
            onClick={(event) => {
              // 地図クリック等のイベント発火を防ぐ
              event.stopPropagation();
              onRemove(mapPlot.id);
            }}
            sx={iconButtonSx}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => {
          // メニュー自体のクリックで背後の地図要素等が反応しないように保護
          e.stopPropagation();
        }}
      >
        <MenuItem
          onClick={(e) => {
            void handleDownloadClick(e, "csv");
          }}
        >
          CSV形式でダウンロード
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            void handleDownloadClick(e, "excel");
          }}
        >
          Excel形式でダウンロード
        </MenuItem>
      </Menu>
    </>
  );
};

/**
 * 現在読み込まれているMapPlotRequestの一覧を表示するコンポーネント
 * CRMモード時のみ表示される
 */
export const LoadedMapPlotBar: React.FC<LoadedMapPlotBarProps> = ({
  mapPlots,
  onDownload,
  onRemove,
}) => {
  if (mapPlots.length === 0) return null;

  return (
    <Box
      sx={{
        py: 0.6,
        px: 1.5,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        📍 表示中のプロットデータ:
      </Typography>
      {mapPlots.map((mapPlot) => (
        <LoadedMapPlotChip
          key={mapPlot.id}
          mapPlot={mapPlot}
          onDownload={onDownload}
          onRemove={onRemove}
        />
      ))}
    </Box>
  );
};
